// The shared transaction list screen (Sales Invoice, Purchase Invoice, Stock
// Transfer, ...). A page supplies its normalised records plus column, filter
// and tab descriptions, and keeps its own fetching, permissions, edit/delete
// handlers and entry form. The form is passed as `children` and rendered
// outside the themed wrapper, so its own terminal design is untouched.
//
// Keep `rows`, `columns`, `tabs` and `filters` memoised on the page.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  AlignJustify,
  ChevronUp,
  Columns,
  Download,
  Filter,
  Plus,
  Printer,
  RotateCcw,
  Search,
  X,
} from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { setheadertoggle } from "../../../redux/toggle";
import { exportExcel, exportPDF, printReport } from "../../../core/utils/reportUtils";
import { Caret, FilterChips, MultiSelect, Toast } from "../../report/ReportParts";
import ReportDrawer from "../../report/ReportDrawer";
import { PRESETS, dmy, intg, pct2, presetRange } from "../../report/reportFormat";
import TransactionTable, { BATCH, isNumCol } from "./TransactionTable";

const DEFAULT_SORTS = [
  { v: "date:-1", t: "Newest first" },
  { v: "date:1", t: "Oldest first" },
  { v: "net:-1", t: "Largest amount" },
  { v: "net:1", t: "Smallest amount" },
];

const TransactionScreen = ({
  title,
  subtitle = "",
  unit = "entries",
  rows = [],
  rowKey = (r) => r.guid,
  dateKey = "date",
  tabs = [],
  columns = [],
  filters = [],
  searchMatch,
  searchPlaceholder = "Search…",
  sortOptions = DEFAULT_SORTS,
  exportName,
  onRefresh,
  canAdd,
  onNew,
  newLabel = "New",
  headerActions,
  canEdit,
  onEdit,
  canDelete,
  onDelete,
  drawerContent,
  // optional (row) => [{ label, primary, onClick }]: more actions in the row's drawer
  drawerActions,
  editLabel = "Edit",
  keysDisabled = false,
  defaultPreset = "today",
  children,
}) => {
  const dispatch = useDispatch();
  const { togglehead } = useSelector((state) => state.headerToggle);
  const renderTooltip = (msg) => <Tooltip id="axt-tip">{msg}</Tooltip>;

  /* ---------------- state ---------------- */
  const initial = presetRange(defaultPreset) || ["", ""];
  const [preset, setPreset] = useState(defaultPreset);
  const [fromDate, setFromDate] = useState(initial[0]);
  const [toDate, setToDate] = useState(initial[1]);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [values, setValues] = useState(() => Object.fromEntries(filters.map((f) => [f.key, []])));
  const [activeTab, setActiveTab] = useState(tabs[0]?.id);
  const [sort, setSort] = useState(() => {
    const [k, d] = String(sortOptions[0]?.v || `${dateKey}:-1`).split(":");
    return { k, d: +d };
  });
  const [hidden, setHidden] = useState(() => columns.filter((c) => c.off).map((c) => c.k));
  const [dense, setDense] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [openPop, setOpenPop] = useState(null);
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [selKey, setSelKey] = useState(null);
  const [shown, setShown] = useState(BATCH);

  const searchRef = useRef(null);
  const toastTimer = useRef(null);

  const toast = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), 2600);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput), 140);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ---------------- filtering ---------------- */
  const q = searchText.trim().toLowerCase();

  // date range, dimension filters and search - everything except the tab
  const base = useMemo(
    () =>
      rows.filter((r) => {
        const d = r[dateKey];
        if (fromDate && (!d || d < fromDate)) return false;
        if (toDate && (!d || d > toDate)) return false;
        for (const f of filters) {
          const v = values[f.key] || [];
          if (v.length && !f.match(r, v)) return false;
        }
        if (q && searchMatch && !searchMatch(r, q)) return false;
        return true;
      }),
    [rows, dateKey, fromDate, toDate, filters, values, q, searchMatch]
  );

  const tab = tabs.find((t) => t.id === activeTab) || tabs[0];
  const tabCounts = useMemo(
    () => Object.fromEntries(tabs.map((t) => [t.id, t.match ? base.filter(t.match).length : base.length])),
    [tabs, base]
  );

  const list = useMemo(() => {
    const out = tab?.match ? base.filter(tab.match) : base.slice();
    out.sort((a, b) => {
      const av = a[sort.k];
      const bv = b[sort.k];
      let c;
      if (typeof av === "number" && typeof bv === "number") c = av - bv;
      else c = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
      // same day (or same amount): the later entry number first
      if (!c) c = String(a.billNo ?? "").localeCompare(String(b.billNo ?? ""), undefined, { numeric: true });
      return c * sort.d;
    });
    return out;
  }, [base, tab, sort]);

  // a new result set starts again from the first batch
  useEffect(() => setShown(BATCH), [list]);

  const visibleCols = columns.filter((c) => !hidden.includes(c.k));

  /* ---------------- chips ---------------- */
  const chips = [];
  if (searchText)
    chips.push({
      label: "Search",
      value: searchText,
      clear: () => {
        setSearchInput("");
        setSearchText("");
      },
    });
  filters.forEach((f) => {
    const v = values[f.key] || [];
    if (!v.length) return;
    chips.push({
      label: f.label,
      value: v.length === 1 ? f.items.find((i) => i.v === v[0])?.t || "1 selected" : `${v.length} selected`,
      clear: () => setValues((s) => ({ ...s, [f.key]: [] })),
    });
  });

  /* ---------------- handlers ---------------- */
  const applyPreset = (id) => {
    setPreset(id);
    const r = presetRange(id);
    if (r) {
      setFromDate(r[0]);
      setToDate(r[1]);
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchText("");
    setValues(Object.fromEntries(filters.map((f) => [f.key, []])));
    setPicked(new Set());
    applyPreset(defaultPreset);
  };

  const onSort = (c) => {
    const k = c.sortKey || c.k;
    setSort((s) => (s.k === k ? { k, d: -s.d } : { k, d: isNumCol(c) || c.type === "date" ? -1 : 1 }));
  };

  const openDrawer = (row) => {
    setSelKey(rowKey(row));
    if (!drawerContent) return;
    setDrawerRow(row);
    requestAnimationFrame(() => requestAnimationFrame(() => setDrawerOpen(true)));
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setDrawerRow(null), 200);
  };

  const onMore = useCallback(() => setShown((s) => Math.min(s + BATCH, list.length)), [list.length]);

  const onPick = (key, on) =>
    setPicked((p) => {
      const n = new Set(p);
      if (on) n.add(key);
      else n.delete(key);
      return n;
    });
  const onPickAll = (on) =>
    setPicked((p) => {
      const n = new Set(p);
      list.forEach((r) => (on ? n.add(rowKey(r)) : n.delete(rowKey(r))));
      return n;
    });

  // selection only ever covers rows that are still in the list
  const pickedRows = useMemo(() => list.filter((r) => picked.has(rowKey(r))), [list, picked, rowKey]);

  /* ---------------- keyboard ---------------- */
  const keyState = useRef({});
  keyState.current = { rowKey, list, selKey, shown, drawerOpen, canAdd, canEdit, onNew, onEdit, openDrawer, closeDrawer };

  useEffect(() => {
    if (keysDisabled) return undefined;
    const typing = () => {
      const a = document.activeElement;
      return a && (a.tagName === "INPUT" || a.tagName === "SELECT" || a.tagName === "TEXTAREA" || a.isContentEditable);
    };
    const onKey = (e) => {
      const s = keyState.current;
      if (e.key === "F3") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (e.key === "Escape") {
        if (s.drawerOpen) s.closeDrawer();
        else setOpenPop(null);
        return;
      }
      if (typing() || e.ctrlKey || e.metaKey || e.altKey) return;
      const rowKey = s.rowKey;
      const idx = s.list.findIndex((r) => rowKey(r) === s.selKey);
      const cur = idx > -1 ? s.list[idx] : null;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!s.list.length) return;
        e.preventDefault();
        const j = idx < 0 ? 0 : Math.max(0, Math.min(s.list.length - 1, idx + (e.key === "ArrowDown" ? 1 : -1)));
        if (j >= s.shown) setShown(Math.min(s.list.length, j + BATCH));
        setSelKey(rowKey(s.list[j]));
      } else if (e.key === "Enter" && cur && !s.drawerOpen) {
        e.preventDefault();
        s.openDrawer(cur);
      } else if ((e.key === "e" || e.key === "E") && cur && s.canEdit) {
        e.preventDefault();
        s.onEdit(cur);
      } else if ((e.key === "n" || e.key === "N") && s.canAdd && s.onNew) {
        e.preventDefault();
        s.onNew();
      }
    };
    const onDoc = (e) => {
      if (!e.target.closest(".axr-ms") && !e.target.closest(".axr-popwrap")) setOpenPop(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [keysDisabled]);

  /* ---------------- export ---------------- */
  const exportCols = visibleCols.map((c) => ({ title: c.t, dataIndex: c.exportKey || c.k }));
  const exportData = (data) => () => ({
    data: data.map((r) => {
      const o = { ...r };
      visibleCols.forEach((c) => {
        if (c.type === "date") o[c.exportKey || c.k] = r[c.k] ? dmy(r[c.k]) : "";
      });
      return o;
    }),
    columns: exportCols,
    Rname: exportName || title,
    fieldMapping: Object.fromEntries(exportCols.map((c) => [c.title, c.dataIndex])),
  });

  const downloadCSV = (data) => {
    const cell = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [visibleCols.map((c) => cell(c.t)).join(",")]
      .concat(
        data.map((r) =>
          visibleCols
            .map((c) => {
              const v = r[c.exportKey || c.k];
              if (isNumCol(c)) return cell(pct2(v));
              if (c.type === "date") return cell(v ? dmy(v) : "");
              return cell(v);
            })
            .join(",")
        )
      )
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${exportName || title}${fromDate ? `_${fromDate}_${toDate}` : ""}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    toast(`CSV downloaded · ${data.length} ${unit}`);
  };

  const rangeText = fromDate && toDate ? `${dmy(fromDate)} → ${dmy(toDate)}` : "All dates";
  const sortValue = `${sort.k}:${sort.d}`;
  const sortList = sortOptions.some((o) => o.v === sortValue)
    ? sortOptions
    : [...sortOptions, { v: sortValue, t: "By column" }];
  const left = list.length - Math.min(shown, list.length);
  const drawer = drawerRow && drawerContent ? drawerContent(drawerRow) : null;

  return (
    <>
      <div className={"axr-screen axt-screen" + (dense ? " axr-dense" : "")}>
        <div className="page-wrapper axr-wrapper">
          <div className="content axr-page">
            {/* ============ PAGE HEAD ============ */}
            <div className="axr-rhead">
              <div className="axr-rtitle">
                <h2>{title}</h2>
                <p>
                  {subtitle}
                  {subtitle ? " · " : ""}
                  <b>{rangeText}</b>
                </p>
              </div>
              <div className="axr-rtools">
                <div className="axr-popwrap">
                  <button
                    type="button"
                    className="axr-iconbtn"
                    aria-expanded={openPop === "cols"}
                    onClick={() => setOpenPop(openPop === "cols" ? null : "cols")}
                    title="Choose columns"
                  >
                    <Columns />
                    Columns
                  </button>
                  {openPop === "cols" && (
                    <div className="axr-pop right">
                      <div className="axr-pop-h">Columns</div>
                      {columns.map((c) => (
                        <label className="axr-pop-opt" key={c.k}>
                          <input
                            type="checkbox"
                            checked={!hidden.includes(c.k)}
                            onChange={(e) =>
                              setHidden((h) => (e.target.checked ? h.filter((x) => x !== c.k) : [...h, c.k]))
                            }
                          />
                          {c.t}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="axr-iconbtn"
                  aria-pressed={showFilters}
                  onClick={() => setShowFilters((v) => !v)}
                  title="Show or hide filters"
                >
                  <Filter />
                  Filters
                </button>
                <span className="axr-sepbar" />
                {onRefresh && (
                  <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                    <button
                      type="button"
                      className="axr-iconbtn"
                      aria-label="Refresh"
                      onClick={() => {
                        onRefresh();
                        toast("List reloaded");
                      }}
                    >
                      <RotateCcw />
                    </button>
                  </OverlayTrigger>
                )}
                <button type="button" className="axr-iconbtn" onClick={() => downloadCSV(list)} title="Download CSV">
                  <Download />
                  CSV
                </button>
                <Link to="#" className="axr-iconbtn" title="Export to Excel" onClick={() => exportExcel(exportData(list))}>
                  <ImageWithBasePath src="assets/img/icons/excel.svg" alt="" />
                  Excel
                </Link>
                <Link to="#" className="axr-iconbtn" title="Export to PDF" onClick={() => exportPDF(exportData(list))}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="" />
                  PDF
                </Link>
                <OverlayTrigger placement="top" overlay={renderTooltip("Print")}>
                  <Link to="#" className="axr-iconbtn" aria-label="Print" onClick={() => printReport(exportData(list))}>
                    <Printer />
                  </Link>
                </OverlayTrigger>
                <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                  <Link
                    to="#"
                    aria-label="Collapse"
                    id="collapse-header"
                    className={"axr-iconbtn" + (togglehead ? " active" : "")}
                    onClick={() => dispatch(setheadertoggle(!togglehead))}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
                {canAdd && (
                  <>
                    <span className="axr-sepbar" />
                    {headerActions}
                    <button type="button" className="axr-btn axr-btn-primary" onClick={onNew}>
                      <Plus />
                      {newLabel}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* ============ FILTERS ============ */}
            {showFilters && (
              <section className="axr-card axr-filters">
                <div className="axr-frow top">
                  <div className="axr-fld axr-searchfld">
                    <label htmlFor="axt-q">Search</label>
                    <Search className="axr-sicon" />
                    <input
                      id="axt-q"
                      ref={searchRef}
                      autoComplete="off"
                      placeholder={searchPlaceholder}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && setSearchText(searchInput)}
                    />
                    {searchInput && (
                      <button
                        type="button"
                        className="axr-clr"
                        aria-label="Clear search"
                        onClick={() => {
                          setSearchInput("");
                          setSearchText("");
                          searchRef.current?.focus();
                        }}
                      >
                        <X />
                      </button>
                    )}
                    <span className="axr-kbd">F3</span>
                  </div>
                  <div className="axr-fld sel">
                    <label htmlFor="axt-preset">Period</label>
                    <select
                      id="axt-preset"
                      value={preset}
                      onChange={(e) => (e.target.value === "custom" ? setPreset("custom") : applyPreset(e.target.value))}
                    >
                      {PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <Caret />
                  </div>
                  <div className="axr-fld">
                    <label htmlFor="axt-from">From date</label>
                    <input
                      id="axt-from"
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        if (toDate && e.target.value && e.target.value > toDate) setToDate(e.target.value);
                        setPreset("custom");
                      }}
                    />
                  </div>
                  <div className="axr-fld">
                    <label htmlFor="axt-to">To date</label>
                    <input
                      id="axt-to"
                      type="date"
                      value={toDate}
                      min={fromDate || undefined}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setPreset("custom");
                      }}
                    />
                  </div>
                  <div className="axr-fbtns">
                    <button
                      type="button"
                      className="axr-btn axr-btn-primary"
                      onClick={() => {
                        setSearchText(searchInput);
                        toast("List refreshed");
                      }}
                    >
                      Search
                    </button>
                    <button
                      type="button"
                      className="axr-btn axr-btn-ghost"
                      onClick={() => {
                        resetFilters();
                        toast("Filters cleared");
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="axr-presets">
                  {PRESETS.filter((p) => p.id !== "custom").map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      className="axr-pchip"
                      aria-pressed={preset === p.id}
                      onClick={() => applyPreset(p.id)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                {filters.length > 0 && (
                  <div className="axr-frow dims" style={{ "--axt-dims": Math.min(5, filters.length) }}>
                    {filters.map((f) => (
                      <MultiSelect
                        key={f.key}
                        id={f.key}
                        label={f.label}
                        items={f.items}
                        value={values[f.key] || []}
                        onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                        openPop={openPop}
                        setOpenPop={setOpenPop}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            <FilterChips chips={chips} onClearAll={resetFilters} />

            {/* ============ LIST ============ */}
            <section className="axr-card axr-tablecard">
              <nav className="axr-tabs" role="tablist">
                {tabs.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className="axr-tab"
                    role="tab"
                    aria-selected={tab?.id === t.id}
                    onClick={() => {
                      setActiveTab(t.id);
                      setOpenPop(null);
                    }}
                  >
                    {t.label} <span className="axr-n">{intg(tabCounts[t.id])}</span>
                  </button>
                ))}
              </nav>

              {pickedRows.length > 0 ? (
                <div className="axt-bulk">
                  <span className="axt-bn">{pickedRows.length} selected</span>
                  <button type="button" onClick={() => printReport(exportData(pickedRows))}>
                    <Printer />
                    Print
                  </button>
                  <button type="button" onClick={() => downloadCSV(pickedRows)}>
                    <Download />
                    Export
                  </button>
                  <span className="axt-bsp" />
                  <button type="button" className="plain" onClick={() => setPicked(new Set())}>
                    Clear selection
                  </button>
                </div>
              ) : (
                <div className="axr-tbar">
                  <span className="axr-count">
                    <b>{intg(list.length)}</b> {unit}
                  </span>
                  <div className="axr-right">
                    <button
                      type="button"
                      className="axr-iconbtn"
                      aria-pressed={dense}
                      onClick={() => setDense((v) => !v)}
                      title="Compact rows"
                    >
                      <AlignJustify />
                      Compact
                    </button>
                    <div className="axr-minisel">
                      <select
                        aria-label="Sort"
                        value={sortValue}
                        onChange={(e) => {
                          const [k, d] = e.target.value.split(":");
                          setSort({ k, d: +d });
                        }}
                      >
                        {sortList.map((o) => (
                          <option key={o.v} value={o.v}>
                            {o.t}
                          </option>
                        ))}
                      </select>
                      <Caret />
                    </div>
                  </div>
                </div>
              )}

              <TransactionTable
                columns={visibleCols}
                list={list}
                shown={shown}
                onMore={onMore}
                sort={sort}
                onSort={onSort}
                rowKey={rowKey}
                selKey={selKey}
                onRowClick={openDrawer}
                picked={picked}
                onPick={onPick}
                onPickAll={onPickAll}
                onEdit={onEdit}
                onDelete={onDelete}
                canEdit={canEdit && !!onEdit}
                editLabel={editLabel}
                canDelete={canDelete && !!onDelete}
                unit={unit}
                emptyTitle={`No ${unit} here`}
                emptyHint={
                  preset === "today"
                    ? "Nothing entered today yet. Pick a wider period, or clear a filter."
                    : "Widen the period, or clear a filter."
                }
                emptyAction={
                  canAdd && onNew ? (
                    <button type="button" className="axr-btn axr-btn-primary" onClick={onNew}>
                      <Plus />
                      {newLabel}
                    </button>
                  ) : null
                }
              />

              <div className="axr-pager">
                <span className="axr-info">
                  {list.length ? (
                    <>
                      Showing <b>{intg(Math.min(shown, list.length))}</b> of <b>{intg(list.length)}</b>
                    </>
                  ) : (
                    "nothing to show"
                  )}
                </span>
                {list.length > 0 && (
                  <span className="axr-info">{left > 0 ? `· ${BATCH} per batch, scroll for more` : "· all loaded"}</span>
                )}
                <div className="axr-mid">
                  {left > 0 && (
                    <button type="button" className="axr-pgb" onClick={onMore}>
                      Load {Math.min(BATCH, left)} more
                    </button>
                  )}
                </div>
              </div>
            </section>

            <footer className="axr-status">
              <span className="axr-sg">
                <span className="axr-kbd">F3</span>Search
              </span>
              {canAdd && onNew && (
                <span className="axr-sg">
                  <span className="axr-kbd">N</span>
                  {newLabel}
                </span>
              )}
              <span className="axr-sg">
                <span className="axr-kbd">↑↓</span>Move
              </span>
              <span className="axr-sg">
                <span className="axr-kbd">Enter</span>Preview
              </span>
              {canEdit && onEdit && (
                <span className="axr-sg">
                  <span className="axr-kbd">E</span>Edit
                </span>
              )}
              <span className="axr-sg">
                <span className="axr-kbd">Esc</span>Close
              </span>
              <span className="axr-sp" />
              <span className="axr-sg">
                {intg(list.length)} of {intg(rows.length)} {unit}
              </span>
            </footer>
          </div>
        </div>

        <ReportDrawer
          content={drawer}
          open={drawerOpen}
          onClose={closeDrawer}
          actions={[
            ...(drawerActions && drawerRow
              ? drawerActions(drawerRow).map((a) => ({
                  ...a,
                  onClick: () => {
                    const r = drawerRow;
                    closeDrawer();
                    a.onClick(r);
                  },
                }))
              : []),
            ...(canEdit && onEdit && drawerRow
              ? [
                  {
                    label: editLabel,
                    primary: true,
                    onClick: () => {
                      const r = drawerRow;
                      closeDrawer();
                      onEdit(r);
                    },
                  },
                ]
              : []),
          ]}
        />
        <Toast message={toastMsg} />
      </div>
      {children}
    </>
  );
};

export default TransactionScreen;
